import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { toastTones } from './Toast.styles';
import { ToastCopyButtonProps } from './ToastCopyButton.props';
import { buildToastReport, copyTextToClipboard } from '../../../utils/toast';

/** How long the button confirms the copy before returning to its resting state. */
export const COPY_FEEDBACK_MS = 2000;

type CopyState = 'idle' | 'copied' | 'failed';

const LABELS: Record<CopyState, string> = {
  idle: 'Copy error details',
  copied: 'Error details copied',
  failed: 'Could not copy — select the text instead',
};

/**
 * Copies the message, the page and the time as one block, so a user can paste
 * the whole thing to an administrator instead of retyping it from memory.
 *
 * Failure is reported on the button itself. Raising a toast about a toast that
 * would not copy is noise, and it would land on top of the error being read.
 */
export const ToastCopyButton: React.FC<ToastCopyButtonProps> = ({ toast, occurredAt, size }) => {
  const theme = useTheme();
  const tones = toastTones(theme);
  const [state, setState] = useState<CopyState>('idle');
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    const copied = await copyTextToClipboard(
      buildToastReport(toast, { href: window.location.href, occurredAt })
    );
    setState(copied ? 'copied' : 'failed');
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState('idle'), COPY_FEEDBACK_MS);
  }, [toast, occurredAt]);

  return (
    <Tooltip title={LABELS[state]}>
      <IconButton
        aria-label={LABELS[state]}
        onClick={handleCopy}
        sx={{
          flexShrink: 0,
          width: size,
          height: size,
          color: state === 'copied' ? tones.success : tones.body,
          '&:hover': { color: tones.title, backgroundColor: tones.hoverOverlay },
        }}
      >
        {state === 'copied' ? (
          <CheckRounded sx={{ fontSize: 18 }} />
        ) : (
          <ContentCopyRounded sx={{ fontSize: 17 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};
